'use client'

/**
 * AI Chat Messages (Issue #4429, #4443, #4435)
 *
 * Message list with streaming support, tool call indicators,
 * markdown rendering, user avatars, and terminology-aware empty state.
 *
 * SHARED by two surfaces with opposite scroll models — the docked
 * `AiChatPanel` (fixed-height track, transcript scrolls) and the Home page
 * (one page scroll, document scrolls). That is what the `scrollable` prop is
 * for; see its doc comment (#13512).
 */

import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import { Wrench, Compass, RefreshCw, CornerDownLeft } from '@/components/icons'
import { MarkdownContent } from '@/components/markdown-content'
import { Avatar } from '@sai/ui/components'
import { Button } from '@/components/ui/button'
import {
  type AiChatContextCard,
  type AiChatSuggestion,
  notifyAiChatSuggestionUsed,
} from '@/lib/ai-chat/page-suggestions'
import { cn } from '@/lib/utils'
import { TranscriptChoicePrompt } from '@/components/chat-choice/transcript-choice-prompt'
import { type ChoiceAnswer, findChoicePrompt } from '@/lib/chat-choice/answer'
import { ASK_CHOICE_TOOL_NAME } from '@/lib/chat-choice/schema'
import type { EntityPin } from '@/lib/ai-chat/resolve-entities'
import type { AiChatUserInfo, ChatMessage } from './ai-chat-provider'
import { useAiChat } from './ai-chat-provider'
import { SentAttachmentChips } from './chat-attachment-chips'
import { AnswerClassBlock } from './answer-class-block'
import { NextSteps } from './next-steps'
import { ScopeQuestion } from './scope-question'
import { type ScopeAnswer, scopeAnswerText } from '@/lib/ai-chat/scope-question'
import { ResolvedBar } from './resolved-bar'
import { NotYetPreview } from './not-yet-preview'
import { RecommendationCard } from './recommendation-card'
import { SaiChatDot } from './sai-chat-dot'
import { SaiChatDotAnimated } from './sai-chat-dot-animated'

interface AiChatMessagesProps {
  messages: ChatMessage[]
  isStreaming?: boolean
  /**
   * Does the TRANSCRIPT own the scroll, or does the caller (#13512)?
   *
   * Defaults to `true`, which is the shape this component shipped with and
   * the only correct shape for the docked `AiChatPanel`: that panel is a
   * viewport-height track (`h-screen h-dvh`, `ai-chat-panel.tsx`), so its
   * height is definite, something inside it has to be the scroller, and
   * `flex-1 overflow-y-auto` is it. (440px is the panel's WIDTH,
   * `sm:w-[440px]` — a different axis, and not the one that decides this.)
   *
   * Home is the other model. It is ONE page scroll, so a transcript that
   * keeps its own scrollport nests a scrollport inside a scrollport — two
   * scrollbars, and which one the wheel drives depends on where the cursor
   * sits. That is the reported #13512 defect, and Home passes `false`.
   *
   * Caller-controlled rather than deleted, and defaulting to the historical
   * value, because the panel must not have to opt back in to behaviour it
   * already had: a new caller that forgets the prop gets a transcript that
   * scrolls inside its container, which is wrong-looking and obvious, rather
   * than one that silently overflows a fixed-height dock.
   *
   * Streaming auto-scroll needs no branch for this. It is `scrollIntoView`
   * on an end sentinel, and that scrolls every scrollable ancestor rather
   * than one this component named — so it follows whichever scroller is
   * actually there.
   */
  scrollable?: boolean
}

export function AiChatMessages({
  messages,
  isStreaming,
  scrollable = true,
}: AiChatMessagesProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const { userInfo, sendMessage } = useAiChat()
  // #15435 — a click on an `ask_choice` question is the person's next turn.
  const answerChoice = (answer: ChoiceAnswer, text: string) => {
    void sendMessage(text, undefined, { choiceAnswer: answer })
  }
  // #16813 — a next-step pivot is a question the asker takes as their turn.
  // #16822 — a choice's re-ask carries the person chosen.
  const askPivot = (question: string, entityPick?: EntityPin) => {
    void (entityPick
      ? sendMessage(question, undefined, { entityPick })
      : sendMessage(question))
  }
  // #16823 — a scoping pick is the asker's next turn, carrying the pick.
  const answerScope = (question: string, answer: ScopeAnswer | null) => {
    void sendMessage(question, undefined, answer ? { scopeAnswer: answer } : {})
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
    })
  }, [messages, isStreaming])

  if (messages.length === 0) {
    return <EmptyState scrollable={scrollable} />
  }

  return (
    <div
      data-testid="ai-chat-transcript"
      className={cn(
        'px-4 py-4',
        // `flex-1` and the scrollport travel together: the height is what
        // there is to overflow, so half of this pair is never right on its
        // own. Under a caller-owned scroll the transcript is an ordinary
        // block that grows with its content.
        scrollable && 'flex-1 overflow-y-auto overscroll-y-contain',
      )}
    >
      <div className="space-y-8">
        {messages.map((msg, i) => (
          <MessageBubble
            key={msg.id ?? i}
            message={msg}
            userInfo={userInfo}
            next={messages[i + 1]}
            choiceDisabled={isStreaming}
            onAnswerChoice={answerChoice}
            onAskPivot={askPivot}
            onAnswerScope={answerScope}
          />
        ))}
        {isStreaming && (
          /*
            `text-status-blue-text`, NOT the bare `text-status-blue` tint
            (#13429). The bare token is `--color-blue-500` (#3b82f6), a
            DECORATIVE tint: against `bg-surface-elevated` (#ffffff) at this
            12px `text-xs` size it measures 3.67:1, under the 4.5:1 WCAG AA
            floor for normal text, and axe fails
            `golden-path-home-conversation-13407` on it by name. The `-text`
            variant is `--color-blue-700` (#1d4ed8) at ~6.7:1, and is what
            every other TEXT site in the app already uses — the bare tint's
            remaining callers are icons (`src/app/admin/data-import/hr-import.tsx`),
            where a sub-AA tint is fine because it is not text.
          */
          <div className="flex items-center gap-2.5 pl-9 text-xs text-status-blue-text">
            <div className="flex gap-1">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-status-blue-bg-strong [animation-delay:0ms]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-status-blue-bg-strong [animation-delay:150ms]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-status-blue-bg-strong [animation-delay:300ms]" />
            </div>
            <span className="font-medium">Thinking...</span>
          </div>
        )}
      </div>
      <div ref={messagesEndRef} />
    </div>
  )
}

function MessageBubble({
  message,
  userInfo,
  next,
  choiceDisabled,
  onAnswerChoice,
  onAskPivot,
  onAnswerScope,
}: {
  message: ChatMessage
  userInfo?: AiChatUserInfo
  /** #15435 — the message after this one, which may answer its question. */
  next?: ChatMessage
  choiceDisabled?: boolean
  onAnswerChoice: (answer: ChoiceAnswer, text: string) => void
  /** #16813 — ask a next-step pivot question as the next turn. */
  onAskPivot: (question: string, entityPick?: EntityPin) => void
  /** #16823 — answer this turn's scoping question as the next turn. */
  onAnswerScope: (question: string, answer: ScopeAnswer | null) => void
}) {
  const isUser = message.role === 'user'
  // #16823 — a CLARIFY turn: its question renders once, as the scope step.
  // A reloaded one has no `precheck`; its step comes off the stored row.
  const asksScope =
    (message.precheck?.nextSteps.some(step => step.kind === 'scope') ??
      false) ||
    message.scopeStep !== undefined
  // A stored call whose input could not be parsed never ran (#15099); a chip
  // would show it as an action that happened. An `ask_choice` call renders
  // as its question instead (#15435).
  const toolChips =
    message.toolCalls?.filter(
      tc => !tc.unparseable && tc.name !== ASK_CHOICE_TOOL_NAME,
    ) ?? []
  // Parsed once per stored calls, not on every streamed delta's re-render.
  const choice = useMemo(
    () => findChoicePrompt(message.toolCalls),
    [message.toolCalls],
  )

  return (
    <div className={cn('flex gap-3', isUser && 'flex-row-reverse')}>
      {isUser ? (
        <div className="shrink-0">
          <UserAvatar userInfo={userInfo} />
        </div>
      ) : (
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
          <SaiChatDot size={24} />
        </div>
      )}
      <div
        className={cn(
          'text-base leading-relaxed text-text-primary',
          isUser
            ? 'max-w-[85%] rounded-2xl rounded-br-none bg-fill-10 px-3 py-2'
            : 'min-w-0 max-w-none flex-1',
        )}
      >
        {toolChips.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1">
            {toolChips.map(tc => {
              const isNav = tc.name === 'navigate_user'
              const isRefresh = tc.name === 'refresh_page'
              return (
                <span
                  key={tc.id}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                    isNav || isRefresh
                      ? 'bg-status-blue-bg/50 text-action-primary'
                      : 'bg-surface-panel text-text-secondary'
                  }`}
                >
                  {isNav ? (
                    <Compass className="h-2.5 w-2.5" />
                  ) : isRefresh ? (
                    <RefreshCw className="h-2.5 w-2.5" />
                  ) : (
                    <Wrench className="h-2.5 w-2.5" />
                  )}
                  {isNav
                    ? 'Navigating...'
                    : isRefresh
                      ? 'Refreshing page...'
                      : tc.name}
                </span>
              )
            })}
          </div>
        )}
        {isUser ? (
          <>
            {/* #16101 — the files this turn was sent with. */}
            {message.attachments?.length ? (
              <SentAttachmentChips attachments={message.attachments} />
            ) : null}
            {message.scopeAnswer ? (
              <ScopePick
                answer={message.scopeAnswer}
                content={message.content}
              />
            ) : (
              <div className="whitespace-pre-wrap break-words">
                {message.content}
              </div>
            )}
          </>
        ) : (
          <>
            {/* #16822 — who this answer is about, before the answer, with
                the switch when another in-scope person shares the name. */}
            {message.resolved && (
              <ResolvedBar
                resolved={message.resolved}
                onAsk={onAskPivot}
                disabled={choiceDisabled}
              />
            )}
            {!asksScope && (
              <MarkdownContent
                content={message.content}
                compact
                className="text-base leading-relaxed prose-p:my-4 prose-ul:my-4 prose-ol:my-4 prose-li:my-2 prose-headings:mt-6 prose-headings:mb-3 [&>:first-child]:mt-0 [&>:last-child]:mb-0 [&_p]:text-inherit [&_li]:text-inherit [&_h1]:text-inherit [&_h2]:text-inherit [&_h3]:text-inherit [&_h4]:text-inherit [&_strong]:text-inherit [&_em]:text-inherit"
              />
            )}
            <TranscriptChoicePrompt
              choice={choice}
              next={next}
              disabled={choiceDisabled}
              onAnswer={onAnswerChoice}
            />
            {/* #14083 — the precheck class the server planned this turn
                against, with each source beside the claim it supports.
                `null` on every surface but Home, where it renders nothing
                and the transcript is what it was. */}
            <AnswerClassBlock
              presentation={message.precheck ?? null}
              unmetNeed={message.unmetNeed ?? null}
            />
            {/* #14086 — the shape of the answer a NOT_YET turn would have
                had, and its one question. Only when the turn wrote a record
                for the answer to land on. */}
            {message.unmetNeed?.preview && (
              <NotYetPreview
                unmetNeedId={message.unmetNeed.id}
                preview={message.unmetNeed.preview}
              />
            )}
            {/* #16813 — what the asker can do next, on every turn whose
                presentation carries steps (every class but GROUNDED). */}
            {message.precheck && message.precheck.nextSteps.length > 0 && (
              <NextSteps
                steps={message.precheck.nextSteps}
                answerClass={message.precheck.answerClass}
                unmetNeedId={message.unmetNeed?.id ?? null}
                onAskPivot={onAskPivot}
                onAnswerScope={onAnswerScope}
                answered={next?.role === 'user'}
                disabled={choiceDisabled}
              />
            )}
            {/* #16823 — a reloaded scoping question, from the stored row. */}
            {!message.precheck && message.scopeStep && (
              <ScopeQuestion
                step={message.scopeStep}
                disabled={choiceDisabled || next?.role === 'user'}
                onAnswer={onAnswerScope}
              />
            )}
            {/* #16826 — at most one use-case recommendation per turn. */}
            {message.recommendation && (
              <RecommendationCard
                key={message.recommendation.recordId}
                recommendation={message.recommendation}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * A scoping pick on the reader's side (#16823): the question they were asked
 * and what they chose, as "Q: … / A: …". The option's label is from a closed
 * table; a free-text pick shows the words they typed.
 */
function ScopePick({
  answer,
  content,
}: {
  answer: ScopeAnswer
  content: string
}) {
  const text = scopeAnswerText(answer, content)
  return (
    <dl data-testid="ai-chat-scope-answer" className="space-y-0.5">
      <div className="flex gap-1.5 text-sm text-text-secondary">
        <dt>Q:</dt> <dd>{text.question}</dd>
      </div>
      <div className="flex gap-1.5">
        <dt className="font-medium">A:</dt>{' '}
        <dd className="whitespace-pre-wrap break-words">{text.answer}</dd>
      </div>
    </dl>
  )
}

/**
 * The panel's own generic starters, used only when the page published none.
 *
 * They are the same three strings the ask bar used to render under itself on
 * `/today`, and the reason they are fine HERE and were not fine there: on the
 * empty panel they are the only thing on screen and they teach the affordance,
 * whereas above the fold they pushed the morning's actual content down to
 * suggest questions nobody's own data prompted.
 */
const GENERIC_SUGGESTIONS = [
  'What needs my attention?',
  'How are we tracking?',
  'Show me at-risk items',
] as const

function EmptyState({ scrollable }: { scrollable: boolean }) {
  // Page-published suggestions win: they name rows this reader is looking at
  // (#13133). See `@/lib/ai-chat/page-suggestions`.
  // `?? []` rather than trusting the field: the provider always supplies it and
  // so does the fail-open default, but a test (or any caller) that mocks
  // `useAiChat` with a partial value must not crash the panel over its starters.
  const { pageSuggestions, pageSuggestionContext, pageContextCard } =
    useAiChat()
  const published = pageSuggestions ?? []
  const suggestions: readonly AiChatSuggestion[] =
    published.length > 0
      ? published
      : GENERIC_SUGGESTIONS.map(question => ({ id: question, question }))
  // A page's suggestion is ASKED on press; a generic starter FILLS THE BOX.
  // They are different offers: one is a question about data the reader is
  // looking at, the other is an example of how to phrase your own. Sending a
  // starter would ask a question nobody's page prompted.
  const isPagePublished = published.length > 0

  return (
    <div
      className={cn(
        'flex flex-col items-center px-8 text-center',
        // Same pairing as the transcript above: the empty state is only a
        // scrollport when it owns a definite height to overflow.
        scrollable && 'flex-1 overflow-y-auto overscroll-y-contain',
        'justify-start pt-16',
      )}
    >
      <div className="mb-6">
        <SaiChatDotAnimated size={52} />
      </div>
      <h3 className="text-xl font-semibold text-text-primary">
        Ask about your data
      </h3>
      <p className="mt-2 max-w-80 text-sm leading-relaxed text-text-secondary">
        Get insights, find patterns, and take action using natural language.
      </p>

      {/* What the page already knows, when it published it (#13813). Between
          the introduction and the questions on purpose: it is the answer to
          "what am I looking at", and the questions beneath it only make sense
          once that has been said. */}
      <PageContextBlock card={pageContextCard ?? null} />

      {/* Suggestion chips — the page's, when it published any. `gap-y-3`:
          each chip's 44px hit area reaches 6px past its painted pill, so a
          row gap under 12px makes stacked or wrapped hit areas overlap. */}
      <div
        data-testid="ai-chat-suggestions"
        className="mt-7 flex w-full flex-col gap-x-2.5 gap-y-3"
      >
        {suggestions.map(suggestion => (
          // A full-width outline `Button` row, not a hand-rolled pill
          // (#13133): the primitive carries the 44px floor and the
          // focus-visible ring a hand-written control had no equivalent of.
          <SuggestionControl
            key={suggestion.id}
            onClick={() => {
              if (!isPagePublished) {
                // Dispatch to the input — uses custom event
                window.dispatchEvent(
                  new CustomEvent('ai-chat:suggest', {
                    detail: suggestion.question,
                  }),
                )
                return
              }
              // Announced BEFORE the send so the publishing page counts the ask
              // even if the turn itself fails.
              notifyAiChatSuggestionUsed(suggestion)
              window.dispatchEvent(
                new CustomEvent('ai-chat:send', {
                  detail: pageSuggestionContext
                    ? {
                        message: suggestion.question,
                        surfaceContext: pageSuggestionContext,
                      }
                    : suggestion.question,
                }),
              )
            }}
          >
            {suggestion.question}
          </SuggestionControl>
        ))}
      </div>
    </div>
  )
}

/**
 * The block the panel leads with when the page published one (#13813).
 *
 * A `<section>` with an accessible name rather than a bare `<div>`: it is a
 * standing landmark above the conversation, and a screen-reader user arriving
 * at the panel needs to be able to tell "what I am looking at" from "what I
 * could ask". LEFT-ALIGNED inside the centred column, because a wrapped
 * summary paragraph centred over three lines is unreadable.
 *
 * `null` when there is no card, which is every page but the meeting record
 * and the note pages — the empty state is then byte-for-byte what it was.
 *
 * Two forms, by the card's `kind` (#14447): a note's card is captioned as a
 * note. Both testids are string literals at this render site, never taken
 * from the card, for the reason `AiChatContextCard` gives.
 *
 * Semantic tokens only and no `dark:` variants: the tokens carry both
 * palettes (`AGENTS.md` § Design System).
 */
function PageContextBlock({ card }: { card: AiChatContextCard | null }) {
  if (card === null) return null

  const facts = card.facts ?? []
  const topics = card.topics ?? []
  const className =
    'mt-6 w-full rounded-xl border border-stroke-extra-light bg-surface-panel p-4 text-left'
  const body = (
    <>
      <h4 className="text-sm font-semibold text-text-primary">{card.title}</h4>
      {facts.length > 0 ? (
        <p className="mt-1 text-xs text-text-secondary">{facts.join(' · ')}</p>
      ) : null}
      {card.summary ? (
        <p className="mt-2 text-sm leading-relaxed text-text-secondary">
          {card.summary}
        </p>
      ) : null}
      {topics.length > 0 ? (
        <ul className="mt-3 flex flex-wrap gap-1.5">
          {topics.map(topic => (
            <li
              key={topic}
              className="rounded-md bg-surface-elevated px-2 py-1 text-xs text-text-secondary"
            >
              {topic}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  )

  if (card.kind === 'note') {
    return (
      <section
        data-testid="ai-chat-note-context"
        aria-label="The note you are looking at"
        className={className}
      >
        {body}
      </section>
    )
  }
  return (
    <section
      data-testid="ai-chat-meeting-context"
      aria-label="The meeting you are looking at"
      className={className}
    >
      {body}
    </section>
  )
}

function SuggestionControl({
  children,
  onClick,
}: {
  children: ReactNode
  onClick: () => void
}) {
  return (
    <Button
      variant="outline"
      className="min-h-12 w-full justify-between rounded-xl px-4 text-left font-normal text-text-secondary shadow-none transition-colors hover:border-stroke-selected hover:bg-surface-panel"
      onClick={onClick}
      aria-label={typeof children === 'string' ? children : undefined}
    >
      <span className="truncate">{children}</span>
      <CornerDownLeft className="h-4 w-4 shrink-0 text-text-secondary" />
    </Button>
  )
}

/**
 * The user's avatar on a user turn — sai-ui `Avatar` since #15021 (phase 4
 * of #14954). It is the only thing in the turn that says who spoke, so it is
 * named on the ROOT (`role="img"` + `aria-label`): the photo's `alt` only
 * exists once the photo has loaded, which would leave the initials fallback
 * unnamed. sai's `Avatar` decides photo-or-initials itself (Radix swaps to
 * the fallback when `/api/people/:id/photo` 404s). The hover card the old
 * avatar suppressed here (`disableHover`) is not drawn either.
 */
function UserAvatar({ userInfo }: { userInfo?: AiChatUserInfo }) {
  const name = userInfo?.name || userInfo?.email || 'You'
  return (
    <Avatar
      name={name}
      url={userInfo ? `/api/people/${userInfo.id}/photo` : null}
      size="sm"
      role="img"
      aria-label={name}
    />
  )
}
